import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, Phone, Mail, ChevronDown, ChevronRight, Truck } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { CONTACT_INFO, categories } from '../../data/mock';
import Logo from '../Logo';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isShopOpen, setIsShopOpen] = useState(false);
  const [isMobileShopOpen, setIsMobileShopOpen] = useState(false);
  const { cartCount, openCart } = useCart();
  const location = useLocation();
  const closeTimer = useRef(null);

  useEffect(() => { setIsMobileMenuOpen(false); setIsShopOpen(false); }, [location]);

  const phoneHref = `tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`;

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Shop', path: '/shop', hasDropdown: true },
    { name: 'About', path: '/about' },
    { name: 'Support', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  const openShop = () => { if (closeTimer.current) clearTimeout(closeTimer.current); setIsShopOpen(true); };
  const scheduleCloseShop = () => { closeTimer.current = setTimeout(() => setIsShopOpen(false), 140); };

  return (
    <header className="sticky top-0 left-0 right-0 z-50">
      {/* Utility bar */}
      <div className="bg-dark-900 text-dark-200 text-[12px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between gap-4">
          <p className="flex items-center gap-2"><Truck className="w-3.5 h-3.5 text-athletic-400" /> Free USPS shipping on every U.S. order</p>
          <div className="hidden sm:flex items-center gap-5">
            <a href={phoneHref} className="flex items-center gap-1.5 hover:text-white transition-colors"><Phone className="w-3.5 h-3.5" /> {CONTACT_INFO.phone}</a>
            <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-center gap-1.5 hover:text-white transition-colors"><Mail className="w-3.5 h-3.5" /> {CONTACT_INFO.email}</a>
          </div>
        </div>
      </div>

      {/* Main row */}
      <div className="bg-white/95 backdrop-blur border-b border-dark-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[76px] gap-4">
            <Logo />

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                link.hasDropdown ? (
                  <div key={link.path} className="relative" onMouseEnter={openShop} onMouseLeave={scheduleCloseShop}>
                    <Link to={link.path} className={`flex items-center gap-1 text-[15px] font-medium transition-colors ${isActive(link.path) ? 'text-athletic-600' : 'text-dark-700 hover:text-athletic-600'}`}>
                      {link.name}<ChevronDown className={`w-4 h-4 transition-transform ${isShopOpen ? 'rotate-180' : ''}`} />
                    </Link>
                  </div>
                ) : (
                  <Link key={link.path} to={link.path} className={`text-[15px] font-medium transition-colors ${isActive(link.path) ? 'text-athletic-600' : 'text-dark-700 hover:text-athletic-600'}`}>{link.name}</Link>
                )
              ))}
            </nav>

            <div className="flex items-center gap-2 sm:gap-3">
              <Link to="/shop" className="hidden xl:inline-flex items-center px-5 py-2.5 rounded-xl bg-athletic-600 text-white text-sm font-semibold hover:bg-athletic-700 transition-colors">
                Shop all tools
              </Link>
              <button
                onClick={openCart}
                data-testid="cart-button"
                className="relative w-11 h-11 rounded-xl flex items-center justify-center text-dark-800 bg-dark-100 hover:bg-athletic-50 hover:text-athletic-700 border border-dark-200 transition-colors"
                aria-label="Cart"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-athletic-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white">
                    {cartCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden w-11 h-11 rounded-xl flex items-center justify-center text-dark-900 bg-dark-100 border border-dark-200 hover:bg-dark-200"
                aria-label="Menu"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Shop mega dropdown (desktop) */}
        {isShopOpen && (
          <div className="hidden lg:block absolute inset-x-0 top-full" onMouseEnter={openShop} onMouseLeave={scheduleCloseShop}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="mt-0 bg-white border border-dark-200 rounded-b-2xl shadow-elevated overflow-hidden">
                <div className="grid grid-cols-3 gap-1 p-4">
                  {categories.map((cat) => (
                    <Link key={cat.id} to={`/shop?category=${cat.slug}`} className="flex items-center gap-3 p-3 rounded-xl hover:bg-dark-50 transition-colors group">
                      <span className="w-14 h-14 rounded-lg overflow-hidden bg-dark-100 flex-shrink-0"><img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" /></span>
                      <span>
                        <span className="block text-[15px] font-semibold text-dark-900 group-hover:text-athletic-600 transition-colors">{cat.name}</span>
                        <span className="block text-xs text-dark-500 line-clamp-1">{cat.description}</span>
                      </span>
                    </Link>
                  ))}
                </div>
                <Link to="/shop" className="flex items-center justify-between px-6 py-3.5 bg-dark-50 border-t border-dark-200 text-sm font-semibold text-athletic-700 hover:bg-athletic-50 transition-colors">
                  Browse the full catalog
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden border-b border-dark-200 bg-white shadow-elevated">
          <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
            {navLinks.map((link) => (
              link.hasDropdown ? (
                <div key={link.path}>
                  <button onClick={() => setIsMobileShopOpen(!isMobileShopOpen)} className="w-full flex items-center justify-between px-4 py-3 text-base font-semibold text-dark-800 hover:bg-athletic-50 hover:text-athletic-600 rounded-xl">
                    {link.name}<ChevronDown className={`w-4 h-4 transition-transform ${isMobileShopOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isMobileShopOpen && (
                    <div className="pl-2 pb-1">
                      {categories.map((cat) => (
                        <Link key={cat.id} to={`/shop?category=${cat.slug}`} className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-dark-600 hover:bg-athletic-50 hover:text-athletic-600 rounded-lg">
                          <span className="w-9 h-9 rounded-lg overflow-hidden bg-dark-100 flex-shrink-0"><img src={cat.image} alt="" className="w-full h-full object-cover" /></span>
                          {cat.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link key={link.path} to={link.path} className="flex items-center justify-between px-4 py-3 text-base font-semibold text-dark-800 hover:bg-athletic-50 hover:text-athletic-600 rounded-xl">{link.name}<ChevronRight className="w-4 h-4 text-dark-300" /></Link>
              )
            ))}
            <a href={phoneHref} className="flex items-center gap-2 mt-3 px-4 py-3 rounded-xl bg-athletic-600 text-white font-semibold justify-center"><Phone className="w-4 h-4" /> Call {CONTACT_INFO.phone}</a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
